import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import AppImage from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ChatWindow = ({ conversation, messages, currentUser, onSendMessage, onBack }) => {
  const [messageInput, setMessageInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (messageInput?.trim()) {
      onSendMessage(messageInput);
      setMessageInput('');
    }
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const groupMessagesByDate = (messages) => {
    const groups = {};
    messages?.forEach(msg => {
      const date = new Date(msg?.created_at)?.toLocaleDateString();
      if (!groups?.[date]) {
        groups[date] = [];
      }
      groups?.[date]?.push(msg);
    });
    return groups;
  };

  const messageGroups = groupMessagesByDate(messages);

  return (
    <div className="flex flex-col h-full">
      {/* Chat Header */}
      <div className="p-4 border-b border-border bg-card flex items-center gap-3">
        <button
          onClick={onBack}
          className="lg:hidden p-2 hover:bg-muted rounded-lg transition-smooth"
          aria-label="Back to conversations"
        >
          <Icon name="ArrowLeft" size={20} />
        </button>
        
        <div className="relative flex-shrink-0">
          {conversation?.participant?.avatar_url ? (
            <AppImage
              src={conversation?.participant?.avatar_url}
              alt={conversation?.participant?.full_name || 'Member'}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              <Icon name="User" size={20} color="var(--color-primary)" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h2 className="font-semibold text-foreground truncate">
            {conversation?.participant?.full_name || 'Unknown Member'}
          </h2>
          {isTyping && (
            <p className="text-sm text-muted-foreground">typing...</p>
          )}
        </div>

        <button
          className="p-2 hover:bg-muted rounded-lg transition-smooth"
          aria-label="More options"
        >
          <Icon name="MoreVertical" size={20} />
        </button>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-muted/20">
        {Object.entries(messageGroups)?.map(([date, msgs]) => (
          <div key={date}>
            {/* Date Separator */}
            <div className="flex items-center justify-center my-4">
              <span className="px-3 py-1 bg-muted rounded-full text-xs text-muted-foreground">
                {date}
              </span>
            </div>

            {/* Messages */}
            {msgs?.map((message) => {
              const isSentByMe = message?.sender_id === currentUser?.id;
              
              return (
                <div
                  key={message?.id}
                  className={`flex items-end gap-2 mb-3 ${
                    isSentByMe ? 'flex-row-reverse' : 'flex-row'
                  }`}
                >
                  {/* Avatar */}
                  {!isSentByMe && (
                    <div className="flex-shrink-0">
                      {message?.sender?.avatar_url ? (
                        <AppImage
                          src={message?.sender?.avatar_url}
                          alt={message?.sender?.full_name || 'Member'}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                          <Icon name="User" size={16} color="var(--color-primary)" />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Message Bubble */}
                  <div
                    className={`max-w-[70%] px-4 py-2 rounded-2xl ${
                      isSentByMe
                        ? 'bg-primary text-primary-foreground rounded-br-sm'
                        : 'bg-card text-foreground rounded-bl-sm'
                    }`}
                  >
                    <p className="text-sm break-words">{message?.content}</p>
                    <span
                      className={`text-xs mt-1 block ${
                        isSentByMe ? 'text-primary-foreground/70' : 'text-muted-foreground'
                      }`}
                    >
                      {formatMessageTime(message?.created_at)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="p-4 border-t border-border bg-card">
        <form onSubmit={handleSubmit} className="flex items-end gap-2">
          <div className="flex-1">
            <textarea
              value={messageInput}
              onChange={(e) => setMessageInput(e?.target?.value)}
              onKeyDown={(e) => {
                if (e?.key === 'Enter' && !e?.shiftKey) {
                  e?.preventDefault();
                  handleSubmit(e);
                }
              }}
              placeholder="Type a message..."
              rows={1}
              className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              style={{ minHeight: '48px', maxHeight: '120px' }}
            />
          </div>
          <Button
            type="submit"
            disabled={!messageInput?.trim()}
            className="h-12 w-12 flex-shrink-0"
            aria-label="Send message"
          >
            <Icon name="Send" size={20} />
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ChatWindow;